import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useOnboardingState } from '@/contexts/OnboardingContext';
import { onboardingService } from '@/services/onboardingService';

// Production polling cadence: 30 attempts × 1s = 30s total budget.
// Exported for tests to override (real timers + tight interval keeps the
// test fast without relying on fake-timer scheduling quirks).
export const STRIPE_SUCCESS_POLL_INTERVAL_MS = 1000;
export const STRIPE_SUCCESS_MAX_POLLS = 30;

/** Route ``/onboarding/stripe/success`` — terminal landing page from Stripe
 * Checkout. Per F031: there is no standalone celebration card; the page
 * polls the onboarding state until the ``checkout.session.completed``
 * webhook flips ``current_step`` to ``'completed'``, then navigates to
 * ``/bot`` where the timeline renders the terminal "all green" state and
 * the Header chip flips to "Bot: ativo".
 *
 * On polling timeout (30s without the webhook landing) we stamp
 * ``stripeOutcome='timed_out'`` so the timeline's ``StripeSection``
 * surfaces a non-fatal banner the user can retry from.
 */
export interface StripeSuccessPageProps {
  /** Override for tests. Don't pass in production code. */
  pollIntervalMs?: number;
  /** Override for tests. Don't pass in production code. */
  maxPolls?: number;
}

export function StripeSuccessPage({
  pollIntervalMs = STRIPE_SUCCESS_POLL_INTERVAL_MS,
  maxPolls = STRIPE_SUCCESS_MAX_POLLS,
}: StripeSuccessPageProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setStripeOutcome, refresh } = useOnboardingState();
  // StrictMode runs effects twice in dev — guard the poll loop so we
  // don't double-fire the navigation on the second mount.
  const dispatchedRef = useRef(false);

  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    async function tick(): Promise<void> {
      if (cancelled) return;
      attempts += 1;
      try {
        // F070 — fresh: the poll waits on a server-side webhook flip; a
        // cached state would stall the loop until the TTL ran out.
        const state = await onboardingService.getState({ fresh: true });
        if (cancelled) return;
        if (state.current_step === 'completed') {
          await refresh().catch(() => {
            // Swallow — the timeline will reload itself on the next mount.
          });
          navigate('/bot', { replace: true });
          return;
        }
      } catch {
        // Transient — keep polling until the budget runs out.
      }
      if (attempts >= maxPolls) {
        setStripeOutcome({ kind: 'timed_out' });
        navigate('/bot', { replace: true });
        return;
      }
      timer = setTimeout(tick, pollIntervalMs);
    }

    tick();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [navigate, refresh, setStripeOutcome, pollIntervalMs, maxPolls]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <p>{t('onboarding.stripe.success.processing')}</p>
      </div>
    </div>
  );
}
